/**
 * Die Wohneinheiten einer Anlage (HM-079, ADR 0026).
 *
 * **Ein Bereich der Anlage, unter „Verwaltung"** — zwischen den Zählern und
 * dem Messkonzept: Eine Wohneinheit sagt, *wem* eine Messstelle zuzurechnen
 * ist, und das Messkonzept (HM-080) steckt aus den Wohneinheiten seine
 * Steckplätze. In der Seitenleiste erscheint er nur, wenn die Anlage ein
 * Mehrfamilienhaus ist oder schon Wohneinheiten hat — ausgeblendet, nicht
 * ausgegraut (Regel 10), und über die Adresse weiterhin erreichbar.
 *
 * **Zugeordnet werden Messstellen, nicht Zähler**, und zwar am Zähler selbst
 * (Stammdaten-Dialog der Detailseite): Wer vor dem Zählerschrank steht, liest
 * die Wohnung vom Zähler ab und nicht die Zähler von der Wohnung. Diese Liste
 * zählt nur, was schon zugeordnet ist.
 *
 * **Die Werte je Wohneinheit fehlen hier bewusst.** Sie entstehen erst aus
 * einem Messkonzept, das die Einheit als Steckplatz führt (HM-080); eine
 * Spalte, die bis dahin überall „—" zeigte, wäre eine Zusage, die die Seite
 * nicht hält.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  breakpoints,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  icons,
  useNarrow,
} from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { SectionHeading } from "../components/section.js";
import { navigate, routePath, type Route } from "../lib/router.js";
import { useSiteAreas } from "../lib/queries.js";
import { meteringPointCountLabel } from "../lib/unit-labels.js";
import { useSiteAccess } from "../lib/site-access.js";

type SiteArea = ApiComponents["schemas"]["SiteArea"];
type CreateSiteAreaRequest = ApiComponents["schemas"]["CreateSiteAreaRequest"];

const CLIP = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;

function unitRoute(siteId: string, unitId: string): Route {
  return { kind: "unit", siteId, unitId, tab: "electricity", period: "day", date: null };
}

export function UnitsScreen({ siteId }: { siteId: string | null }) {
  const [formOpen, setFormOpen] = useState(false);
  // Was die Teilung nicht erlaubt, fehlt (HM-160, Regel 10).
  const rights = useSiteAccess();
  const areas = useSiteAreas(siteId);
  const list = areas.data?.items ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26, maxWidth: 1100 }}>
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
        <SectionHeading
          title="Wohneinheiten"
          subtitle="Wohnungen und Einheiten dieser Anlage — und welche Messstellen ihnen zugeordnet sind."
          action={
            rights.canWrite ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={siteId === null}
                onClick={() => setFormOpen(!formOpen)}
              >
                <icons.IconPlus size={15} />
                Wohneinheit anlegen
              </Button>
            ) : undefined
          }
        />

        {rights.canWrite && formOpen && siteId !== null && (
          <UnitForm
            siteId={siteId}
            hasSelfOccupied={list.some((area) => area.selfOccupied)}
            onDone={() => setFormOpen(false)}
          />
        )}

        <Card padding="none">
          {areas.isPending && siteId !== null && (
            <div style={{ padding: "var(--sp-4)" }}>Wird geladen …</div>
          )}

          {(siteId === null || (!areas.isPending && list.length === 0)) && (
            <EmptyState title="Noch keine Wohneinheit" icon={<icons.IconDoor size={30} />}>
              Eine Wohneinheit ist eine Wohnung oder eine Gewerbeeinheit dieser Anlage. Ihr
              werden Messstellen zugeordnet — am Zähler, in dessen Stammdaten. Mit den
              Wohneinheiten kann ein Mieterstrom-Messkonzept seine Steckplätze bilden.
            </EmptyState>
          )}

          {siteId !== null && list.length > 0 && <UnitTable siteId={siteId} areas={list} />}
        </Card>
      </section>
    </div>
  );
}

/**
 * Die Tabelle — dieselbe Bauweise wie die Zählerliste: Der bedienbare Teil
 * ist der Name als `<a href>`, die Zeile folgt ihm nur für die Maus.
 */
function UnitTable({ siteId, areas }: { siteId: string; areas: readonly SiteArea[] }) {
  const narrow = useNarrow(breakpoints.table);
  const columns = narrow ? "minmax(0, 1fr) auto" : "1.6fr 1fr 0.8fr 1fr auto";

  return (
    <div role="table" aria-label="Wohneinheiten">
      <div
        role="row"
        style={{
          display: "grid",
          gridTemplateColumns: columns,
          gap: "var(--sp-3)",
          alignItems: "center",
          padding: "11px var(--sp-5)",
          borderBottom: "1px solid var(--border-subtle)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "var(--tracking-wide)",
          textTransform: "uppercase",
          color: "var(--text-faint)",
        }}
      >
        <span role="columnheader">Wohneinheit</span>
        {!narrow && <span role="columnheader">Lage</span>}
        {!narrow && <span role="columnheader">Wohnfläche</span>}
        {!narrow && <span role="columnheader">Messstellen</span>}
        <span role="columnheader" />
      </div>

      {areas.map((area, index) => (
        <div
          key={area.id}
          role="row"
          style={{
            display: "grid",
            gridTemplateColumns: columns,
            gap: "var(--sp-3)",
            alignItems: "center",
            padding: "13px var(--sp-5)",
            borderBottom: index < areas.length - 1 ? "1px solid var(--border-subtle)" : "none",
            cursor: "pointer",
          }}
          onClick={() => navigate(unitRoute(siteId, area.id))}
          onMouseEnter={(event) => {
            event.currentTarget.style.background = "var(--surface-inset)";
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = "transparent";
          }}
        >
          <div role="cell" style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: "var(--radius-sm)",
                background: "var(--surface-inset)",
                color: "var(--text-muted)",
              }}
            >
              <icons.IconDoor size={17} />
            </span>
            <span style={{ minWidth: 0 }}>
              <a
                href={routePath(unitRoute(siteId, area.id))}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
                    return;
                  }
                  event.preventDefault();
                  navigate(unitRoute(siteId, area.id));
                }}
                style={{
                  display: "block",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--text-strong)",
                  textDecoration: "none",
                  ...CLIP,
                }}
              >
                {area.name}
              </a>
              {narrow && (
                <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)", ...CLIP }}>
                  {[area.location, meteringPointCountLabel(area.meteringPointCount)]
                    .filter((part) => part !== null)
                    .join(" · ")}
                </span>
              )}
            </span>
          </div>

          {!narrow && (
            <span role="cell" style={{ fontSize: 13, color: "var(--text-muted)", ...CLIP }}>
              {area.location ?? ""}
            </span>
          )}
          {!narrow && (
            <span role="cell" style={{ fontSize: 13, color: "var(--text-muted)", ...CLIP }}>
              {area.livingAreaSqm === null ? "" : `${formatArea(area.livingAreaSqm)} m²`}
            </span>
          )}
          {!narrow && (
            <span role="cell" style={{ fontSize: 13, color: "var(--text-muted)", ...CLIP }}>
              {meteringPointCountLabel(area.meteringPointCount)}
            </span>
          )}
          <span role="cell">
            {/* **Nur die Eigennutzung trägt eine Marke.** Ein Zustand hat eine
                Wohneinheit nicht — sie ist da oder nicht. */}
            {area.selfOccupied && <Badge tone="neutral">selbstgenutzt</Badge>}
          </span>
        </div>
      ))}
    </div>
  );
}

function formatArea(value: number): string {
  return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(value);
}

/**
 * Eine Wohneinheit anlegen.
 *
 * **Der Haken „selbstgenutzt" steht nur, wenn ihn noch keine trägt.** Je Anlage
 * ist höchstens eine Einheit selbstgenutzt — das setzt die Datenbank durch;
 * hier wird ein Haken ausgeblendet, der mit `409` antworten würde (Regel 10).
 */
function UnitForm({
  siteId,
  hasSelfOccupied,
  onDone,
}: {
  siteId: string;
  hasSelfOccupied: boolean;
  onDone: () => void;
}) {
  const client = useQueryClient();
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [livingArea, setLivingArea] = useState("");
  const [selfOccupied, setSelfOccupied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const area = parseArea(livingArea);
  const valid = name.trim().length > 0 && area !== undefined;

  const create = useMutation({
    mutationFn: async (): Promise<SiteArea> => {
      const body: CreateSiteAreaRequest = {
        name: name.trim(),
        selfOccupied,
        ...(location.trim().length > 0 ? { location: location.trim() } : {}),
        ...(area !== null && area !== undefined ? { livingAreaSqm: area } : {}),
      };
      const { data, error: failure } = await api.POST("/sites/{siteId}/site-areas", {
        params: { path: { siteId } },
        body,
      });
      if (failure || !data) {
        throw new Error(apiErrorMessage(failure) ?? "Die Wohneinheit ließ sich nicht anlegen.");
      }
      return data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["site-areas"] });
      onDone();
    },
    onError: (failure: Error) => setError(failure.message),
  });

  return (
    <Card>
      <form
        style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)" }}
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
            gap: "var(--sp-3)",
          }}
        >
          <Input
            label="Bezeichnung"
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
            placeholder="Wohnung 2"
            required
          />
          <Input
            label="Lage"
            value={location}
            maxLength={120}
            hint={'„EG links", „2. OG" — kein Teil des Namens.'}
            onChange={(event) => setLocation(event.target.value)}
          />
          <Input
            label="Wohnfläche in m²"
            value={livingArea}
            inputMode="decimal"
            hint="Freiwillig. Geht in keine Rechnung ein."
            error={area === undefined ? "Bitte eine Zahl größer als null." : undefined}
            onChange={(event) => setLivingArea(event.target.value)}
          />
        </div>

        {!hasSelfOccupied && (
          <Checkbox
            checked={selfOccupied}
            onChange={setSelfOccupied}
            label="Vom Besitzer der Anlage selbst bewohnt"
          />
        )}

        {error !== null && <Alert tone="danger">{error}</Alert>}

        <div style={{ display: "flex", gap: "var(--sp-2)" }}>
          <Button type="submit" disabled={!valid || create.isPending}>
            {create.isPending ? "Wird angelegt …" : "Wohneinheit anlegen"}
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * `null` für leer, `undefined` für Unsinn, sonst die Zahl — dieselbe Prüfung
 * wie bei der Wohnfläche der Anlage: Bei Unsinn wird gar nicht abgeschickt,
 * statt still `null` zu senden (Regel 9). Ein Komma gilt als Dezimaltrenner.
 */
export function parseArea(raw: string): number | null | undefined {
  const text = raw.trim();
  if (text.length === 0) return null;
  const value = Number(text.replace(",", "."));
  return Number.isFinite(value) && value > 0 ? value : undefined;
}
